import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Paper,
  Divider,
  useTheme,
  Stack,
  Avatar,
  useMediaQuery
} from "@mui/material";
import {
  EmojiEvents as ExcellenceIcon,
  AutoAwesome as InnovationIcon,
  GppGood as IntegrityIcon,
  People as CustomerIcon,
  GroupWork as TeamworkIcon
} from "@mui/icons-material";

const MissionPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const coreValues = [
    {
      icon: <ExcellenceIcon fontSize="large" />,
      title: "Excellence",
      description: "We strive for the highest standards in everything we do."
    },
    {
      icon: <InnovationIcon fontSize="large" />,
      title: "Innovation",
      description: "We embrace creativity and continuous improvement."
    },
    {
      icon: <IntegrityIcon fontSize="large" />,
      title: "Integrity",
      description: "We act with honesty, transparency, and fairness."
    },
    {
      icon: <CustomerIcon fontSize="large" />,
      title: "Customer Focus",
      description: "We put customers at the heart of our decisions."
    },
    {
      icon: <TeamworkIcon fontSize="large" />,
      title: "Teamwork",
      description: "We believe in collaboration and shared success."
    }
  ];

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 },
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #f0f4f8 100%)`
    }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box textAlign="center" mb={8}>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            Our Corporate Philosophy
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ 
              maxWidth: 720, 
              mx: "auto", 
              mt: 2, 
              lineHeight: 1.7,
              fontSize: '1.1rem'
            }}
          >
            We are committed to delivering premium dairy products through
            sustainability, innovation, and unwavering ethical standards.
          </Typography>
          <Box sx={{
            width: 80,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            mx: "auto",
            my: 3,
            borderRadius: 2
          }} />
        </Box>

        {/* Mission & Vision Section */}
        <Grid container spacing={4} mb={10}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              background: 'white',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
              borderLeft: `6px solid ${theme.palette.primary.main}`,
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
              }
            }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white'
                }}>
                  <InnovationIcon fontSize="large" />
                </Box>
                <Typography variant="h5" fontWeight={600} color="primary">
                  Our Mission
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.8,
                pl: { xs: 0, sm: 2 },
                position: 'relative',
                '&::before': {
                  content: '"“"',
                  position: 'absolute',
                  left: -10,
                  top: -15,
                  fontSize: '3rem',
                  color: theme.palette.primary.light,
                  fontFamily: 'serif',
                  lineHeight: 1
                }
              }}>
                To deliver high-quality, affordable dairy products made from locally sourced milk, 
                while ensuring sustainability and excellence in every aspect of production.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{
              p: 4,
              height: '100%',
              borderRadius: 4,
              background: 'white',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
              borderLeft: `6px solid ${theme.palette.secondary.main}`,
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
              }
            }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.dark})`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white'
                }}>
                  <ExcellenceIcon fontSize="large" />
                </Box>
                <Typography variant="h5" fontWeight={700} color="secondary">
                  Our Vision
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.8,
                pl: { xs: 0, sm: 2 },
                position: 'relative',
                '&::before': {
                  content: '"“"',
                  position: 'absolute',
                  left: -10,
                  top: -15,
                  fontSize: '3rem',
                  color: theme.palette.secondary.light,
                  fontFamily: 'serif',
                  lineHeight: 1
                }
              }}>
                To become Bhutan's leading producer of premium dairy products, 
                recognized for innovation, sustainability, and customer satisfaction.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Core Values Section */}
        <Box>
          <Box textAlign="center" mb={6}>
            <Typography variant="h4" fontWeight={800} sx={{
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              Our Core Values
            </Typography>
            <Box sx={{
              width: 60,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              mx: "auto",
              my: 2,
              borderRadius: 2
            }} />
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ 
                maxWidth: 680, 
                mx: "auto", 
                fontSize: "1.1rem", 
                lineHeight: 1.6 
              }}
            >
              These values guide our actions, decisions, and the way we interact with customers and partners.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {coreValues.map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    background: 'white',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.03)',
                    borderTop: `4px solid ${theme.palette.primary.main}`,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                      borderTop: `4px solid ${theme.palette.secondary.main}`
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: 70,
                      height: 70,
                      bgcolor: 'transparent',
                      background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                      color: 'white',
                      mx: 'auto',
                      mb: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(5deg)',
                        background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`
                      }
                    }}
                  >
                    {value.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default MissionPage;